/**
 * Assets Collection
 *
 * Much of the DAM API operates on sets of assets. This Assets collection is home to many of those API methods,
 * and all other asset collection classes inherit from this one.
 * @class
 */
import _ from 'underscore';
import PagerModel from '@netx/core-app/models/pager';
import CategoryModel from '@netx/core-category/models/category';
import {getCurrentPortal} from '@netx/core-cms-next/utils/getCurrentPortal';
import {localStore} from '@netx/core-next/instance/localStore';
import {getPrefixedLogger} from '@netx/core-next/instance/logger';
import {URL} from '@netx/core-next/url';
import SearchRulesCollection from '@netx/core-search/collections/searchRules_assets';
import {
	facetSupports,
	searchProperties,
	systemFacets,
} from '@netx/core-search/properties/search';
import Base from '@netx/core/lib/netx-collection';
import {escapeURI} from '@netx/core/utils/escapeURI';
import {unescapeURI} from '@netx/core/utils/unescapeURI';
import customConfig from '../config';
import Model from '../models/asset';
import AssetSetModel from '../models/assetSet';
import {types as assetSetTypes} from '../properties/assetSets';

const logger = getPrefixedLogger('collections/assets');

export default class Assets extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/**
	 * this.fetchSpecificMethod defines the API call for fetching specific asset objects of this type; it will be overridden in Netx.Collections.MiniAssets
	 * @type {string}
	 * @default
	 */
	get fetchSpecificMethod() {
		return 'getAssetObjects';
	}

	/**
	 * The following RPC methods cause the DAM to operate on the assets described in the collection.
	 * All manipulation takes place in the DAM; the browser does not directly manipulate image
	 * data, files or the database.
	 * @rpcs
	 */
	get methods() {
		return {
			read(opts) {
				this.setStartPosition();

				// An empty search was sent
				if (!this.selection.length) {
					this.pager.set({
						itemTotal: 0,
					});
					return new $.Deferred().resolve([]);
				}

				const search = this.selection.createSearchObject();

				// Configure paging:
				// if chunk-fetch is requested, use chunk logic to determine the start & length.
				if (opts.chunk) {
					this._startPosition = opts.refetch
						? this.selection.startPosition
						: this.selection.startPosition + this.length;
					this._maxItems = opts.refetch
						? this.length + this.chunkSize
						: this.selection.maxItems;
				}
				// Otherwise use the standard values.
				else {
					this._startPosition = this.selection.startPosition;
					this._maxItems = this.selection.maxItems;
				}

				if (this.useFacetedSearch()) {
					// Faceted
					this._filterMode = this.selection.searchFacets.filterMode;
					return [
						_.contains(search.elementTypes, 200)
							? 'searchAssetsByAttributeHistory'
							: 'facetedSearch',
						search.sortField,
						search.sortOrder,
						search.matchCriteria,
						search.elementTypes,
						search.elementSubType1s,
						search.elementSubType2s,
						search.elementValue1s,
						search.elementValue2s,
						search.elementValue3s,
						this._startPosition,
						this._maxItems,
						this.selection.searchFacets.selectedValues(),
						this._filterMode,
						-1,
					];
				} else {
					// Default
					return [
						'searchAssetObjects',
						search.sortField,
						search.sortOrder,
						search.matchCriteria,
						search.elementTypes,
						search.elementSubType1s,
						search.elementSubType2s,
						search.elementValue1s,
						search.elementValue2s,
						search.elementValue3s,
						search.saveSearch,
						search.notifyType,
						search.ignoreStat,
						this._startPosition,
						this._maxItems,
					];
				}
			},

			/**
			 * Given an array of ids, fetch the full asset beans
			 * @method
			 */
			fetchSpecificAssets(assetIds, opts) {
				return [this.fetchSpecificMethod, assetIds];
			},

			/**
			 * Soon this will go away as we will get `size` on all our calls
			 * @method
			 */
			fetchAssetCount(opts) {
				// Cleared in parser
				this.fetchingAssetCount = true;

				const search = this.selection.createSearchObject();
				return [
					'searchAssetListLength',
					search.sortField,
					search.sortOrder,
					search.matchCriteria,
					search.elementTypes,
					search.elementSubType1s,
					search.elementSubType2s,
					search.elementValue1s,
					search.elementValue2s,
					search.elementValue3s,
					search.saveSearch,
					search.notifyType,
					search.ignoreStat,
				];
			},

			/**
			 * Rather than get full asset beans from a search, just fetch the asset ids
			 * @method
			 */
			getAssetIds(opts) {
				const search = this.selection.createSearchObject();
				return [
					'searchAssetData',
					search.sortField,
					search.sortOrder,
					search.matchCriteria,
					search.elementTypes,
					search.elementSubType1s,
					search.elementSubType2s,
					search.elementValue1s,
					search.elementValue2s,
					search.elementValue3s,
					search.saveSearch,
					search.notifyType,
					search.ignoreStat,
					// Start at the first one and get eeeeeeeverything
					0,
					0,
				];
			},

			/**
			 * Essentially the same as calling fetchSpecificAssets, only the parser will not set it on this
			 * It is to fetch assets on the fly by id without affecting the collection
			 * @method
			 * @deprecated
			 */
			// Appears unused.
			getAssetObjects() {
				console.warn('assets.getAssetObjects() deprecated, gone in 8.13');
				return ['getAssetObjects', 'getAssetObjectIds'];
			},

			/**
			 * getMostViewedAssets(): like fetch(), but this fetches a set of assets from the DAM that cannot yet be obtained by searchAssetListObjects()
			 * @method
			 */
			getMostViewedAssets() {
				return ['getMostViewedAssets', customConfig.MOST_VIEWED_SIZE];
			},

			/**
			 * getRecentlyUploadedAssets(): like fetch(), but this fetches a set of assets from the DAM that cannot yet be obtained by searchAssetListObjects()
			 * @method
			 */
			getRecentlyUploadedAssets() {
				return [
					'getRecentlyUploadedAssets',
					customConfig.RECENTLY_UPLOADED_SIZE,
				];
			},
			/**
			 * createPDF(): generate a PDF of assets on the DAM, and prepare it for download/share.
			 * @method
			 * @deprecated
			 */
			createPDF(opts) {
				console.warn('assets.createPDF deprecated, gone in 8.12!'); // the share controller does this a whole different way now.
				const search = this.selection.createSearchObject();
				const pdf = this.pdf;
				return [
					'createPDFFromSearch',
					search.sortField,
					search.sortOrder,
					search.matchCriteria,
					search.elementTypes,
					search.elementSubType1s,
					search.elementSubType2s,
					search.elementValue1s,
					search.elementValue2s,
					search.elementValue3s,
					pdf.templateName,
					pdf.titleStr,
					pdf.hiRes,
					pdf.pdfExportPreset,
				];
			},

			/**
			 * createPDFWithOptions(): like createPDF() but with more options.
			 * @method
			 * @deprecated
			 */
			createPDFWithOptions(opts) {
				console.warn('assets.createPDFWithOptions deprecated, gone in 8.12!');
				const cpwo = this.cpwo;
				return [
					'createPDFWithOptions',
					// the share controller does this a whole different way now.
					this.ids(),
					cpwo.sortField,
					cpwo.portrait,
					cpwo.cols,
					cpwo.rows,
					cpwo.attrNames,
					cpwo.attrLines,
					cpwo.highRes,
					cpwo.templateName,
					cpwo.titleStr,
					cpwo.pdfExportPreset,
				];
			},
		};
	}

	/** @type {object} */
	get parsers() {
		return {
			/**
			 * @method
			 * @param {object} result Result
			 * @param {object} opts Request options
			 */
			read(result, opts) {
				// Did we fetch faceted, or normal?
				const facetedResult = this.useFacetedSearch();
				let assets = result.results || result;

				// DAM-15036 - Omit any assets without valid assetIds
				assets = _.filter(assets, (a) => {
					if (!a.assetId) {
						logger.warn('server returned a bogus asset');
					}
					return a.assetId;
				});

				// Check length of results; did we reach the end yet?
				if (assets.length > this.chunkSize) {
					assets.pop();
					this.lastChunk = false;
				} else {
					this.lastChunk = true;
				}

				// Don't remove existing assets if we are doing a chunk fetch.
				// (This options object will be passed to set.)
				opts.remove = !opts.chunk;

				if (facetedResult) {
					const systemFacetLabels = systemFacets;
					const facets = _.map(result.facets, (facet) =>
						_.extend(facet, {
							title: systemFacetLabels[facet.label] || facet.label,
						}),
					);

					// Only return facets that have options with a count
					this.selection.searchFacets.set(facets);
					this.selection.searchFacets.itemTotal = result.size;

					this.pager.set({
						itemTotal: result.size,
					});
				}

				return assets;
			},
			/**
			 * fetchSpecificAssets returns an array of asset beans
			 * @method
			 * @param {array} assets
			 */
			fetchSpecificAssets(assets, assetIds, opts) {
				// Omit any assets without valid assetIds
				this.set(
					_.filter(assets, (asset) => asset.assetId),
					opts,
				);
			},

			/**
			 * fetchAssetCount returns simply a count (will one day be usurped by the `size` paramater)
			 * @method
			 * @param {number}
			 */
			fetchAssetCount(count, opts) {
				if (count == null) {
					throw new Error('bad return value from getAssetCount');
				}
				this.pager.set({
					itemTotal: count,
				});

				// Clear up this flag
				delete this.fetchingAssetCount;
			},
			/**
			 * Sets assetIds property and returns the array of assetIds
			 * @param  {array} assetIds Array of assetIds contained in collection
			 * @return {array}          Array of assetIds contained in collection
			 */
			getAssetIds(assetIds) {
				this.assetIds = assetIds;
				return assetIds;
			},
			/**
			 * getMostViewedAssets returns an array of asset beans
			 * @method
			 * @param {array} assets
			 * @todo Any reason why we are using reset instead of set - I know reset is faster but bypasses all add/remove/merge events
			 */
			getMostViewedAssets(assets) {
				this.reset(assets);
			},

			/**
			 * getRecentlyUploadedAssets returns an array of asset beans
			 * @method
			 * @param {array} assets
			 * @todo Any reason why we are using reset instead of set - I know reset is faster but bypasses all add/remove/merge events
			 */
			getRecentlyUploadedAssets(assets) {
				this.reset(assets);
			},
		};
	}

	/** @override */
	constructor(models, opts = {}) {
		// This is related to a circular reference somewhere
		// Anytime you find that a require at the top results in an empty object - this is why
		// https://stackoverflow.com/questions/37127083/can-two-files-have-each-other-as-dependencies-in-javascript-when-using-webpack
		//Model = '../models/asset');
		opts.model = opts.model || Model;

		super(models, opts);

		this.localStoragePathKey =
			opts.localStoragePathKey ||
			`${URL.host}-${getCurrentPortal()?.path ?? 'app'}`;

		/**
		 * When getAssetIds() is called this property is updated with an array of all asset ids in the collection
		 * @memberof Netx.Collections.Assets
		 * @alias assetIds
		 * @type {array}
		 */
		this.assetIds = opts.assetIds || [];

		/**
		 * @type {number}
		 * @default
		 */
		this.chunkSize = parseInt(opts.chunkSize) || customConfig.CHUNK_SIZE;

		/**
		 * @type {number}
		 * @default
		 */
		this.curPage = opts.curPage || 0;
		this.curStartIndex = opts.curStartIndex ?? null;

		// An Assets collection can manage paged fetching of its assets. If the value 'chunkSize' is smaller than the value 'pageSize',
		// chunk loading is enabled.
		// TODO: use a Netx.Models.Pager here.
		/**
		 * @type {number}
		 * @default
		 */
		this.pageSize = parseInt(opts.pageSize) || customConfig.PAGE_SIZE;

		/**
		 * @type {string}
		 * @default
		 */
		this.routeContext = opts.routeContext || '';

		/**
		 * For reserve
		 * @type {number}
		 * @default
		 */
		this.reserveCount = 0;

		/**
		 * This will contain a SearchRules collection to describe the set of assets this collection will fetch.
		 * @memberof Netx.Collections.Assets
		 * @alias selection
		 * @type {Netx.Collections.SearchRules}
		 */
		this.selection = opts.selection || new SearchRulesCollection();
		this.addSubCollection(this.selection);
		delete opts.selection;

		// We have this problem that a sort runs whenever a model changes - even if the change does not require a sort (for example: inCurrentCart)
		this.sortableChange = true; //false;

		this.pager = new PagerModel(
			{
				pageSize: this.pageSize,
				pagesToDisplay: 10,
				// I would really like to just do !( opts.pagerRoutes ) but lint yells about confusing use of `!`
				routesToPage: opts.pagerRoutes === false ? false : true,
			},
			{
				collection: this,
			},
		);
		this.addSubModel(this.pager);

		/**
		 * (because we can't pass strings directly in backbone.rpc method defs)
		 * (because ... backbone.rpc!)
		 * @property {object} categorizeArgs - Arguments for the batchCategorizeAssets rpc call
		 * @property {string} categorizeArgs.add       - Because RPC
		 * @property {string} categorizeArgs.move      - Because RPC
		 * @property {string} categorizeArgs.remove    - Because RPC
		 * @property {number} categorizeArgs.targetCat - Category id
		 * @property {string} categorizeArgs.action    - Action to take ("Add", "Move", "Remove")
		 */
		this.categorizeArgs = {
			add: 'add',
			move: 'move',
			remove: 'remove',
			targetCat: 0,
			action: '',
		};

		// TODO: Maybe SearchRules should manage this, aggregating these events into some sort of 'rulesChanged' event.
		// Or mabye Netx.Collection should do that generically.

		// Sometimes we fetch in 'faceted search' mode.
		const useFS =
			opts.useFacetedSearch != null
				? opts.useFacetedSearch === true
				: customConfig.USE_FACETED_SEARCH;
		const fsOpts = opts.facetedSearchOptions || {};
		this.useFacetedSearch(useFS, fsOpts);

		// Find & use any selection params set in options
		if (!_.isEmpty(opts)) {
			// are any of the options searchRule attributes?
			_.each(this.selection.at(0).keys(), (p) => {
				if (!opts[p]) return;

				if (_.isArray[p]) {
					// TODO: make this work for multidimensional options (more than one search rule specified) ?
				} else {
					this.selection.at(0).set(p, opts[p]);
				}
			});
			// are any of the options searchRules properties?
			_.each(searchProperties, (p) => {
				if (opts[p] == null) return;

				this.selection[p] = opts[p];
			});
			// are we attempting to set maxItems, which is not allowed in this context?
			if (opts.maxItems != null) {
				console.warn(
					'Error: attempting to set maxItems in search rules collection, but it will be ignored. Set pageSize or chunkSize on asset collection instead.',
				);
			}
		}

		// this.chunkSize is used for fetching a subset of a page, for endless scroll & such.
		// allow chunkSize to change.
		//
		this.fetch = _.wrap(this.fetch, (origFetch, opts) => {
			if (this.chunkSize) {
				this.selection.maxItems = this.chunkSize + 1;
			}
			return origFetch.call(this, opts);
		});

		// For fetching assets on the fly without affecting this collection
		this.getAssetObjectIds = [];

		// When our selection changes, our contents are no longer valid
		const invalidate = () => {
			this.stopListening(this, 'useFaceted');

			delete this.fetchingAssetCount;
			if (this.length) {
				// clear out our contents, so subsequent fetch() runs faster.
				this.reset();
			}
		};

		// Hey, listen!
		this
			// We have an extra fetch method that needs to simulate standard fetch events:
			.listenTo(
				this,
				'request:fetchSpecificAssets',
				(collection, xhr, opts) => {
					if (!opts.silent) {
						this.trigger('request:read', this, xhr, opts);
					}
				},
			)
			.listenTo(
				this,
				'complete:fetchSpecificAssets',
				(collection, result, opts) => {
					if (!opts.silent) {
						this.trigger('sync', this, this.models, opts);
					}
				},
			)
			// When a faceted search happens, this.selection.searchFacets wants to mirror our fetch events:
			.listenTo(this, 'request:read', (collection, xhr, opts) => {
				opts = opts || {};
				if (!opts.silent && this.useFacetedSearch()) {
					this.selection.searchFacets.trigger(
						'request:read',
						this.selection.searchFacets,
						xhr,
						opts,
					);
				}
			})
			// When we sync listen for change in using faceted
			.listenTo(this, 'sync', (collection, result, opts) => {
				this.stopListening(this, 'useFaceted');
				this.listenTo(
					this,
					'useFaceted',
					_.throttle((state) => {
						this.fetch();
					}, 150),
				);
				// If so instructed, cache our selection after a successful sync.
				// (This allows Netx.assets.cursor to explore the same set of assets.)
				if (opts.cacheSelection) {
					dispatcher.trigger('asset:cursor:cacheFetch', this);
				}
				if (!opts.silent && this.useFacetedSearch()) {
					this.selection.searchFacets.trigger(
						'sync',
						this.selection.searchFacets,
						result,
						opts,
					);
				}
			})
			// Always trigger a 'counted' event when this.pager.itemTotal changes to a defined value,
			// or a 'countUnknown' event when we no longer have a correct value.
			.listenTo(this.pager, 'change:itemTotal', (m, count) => {
				count != null
					? this.trigger('counted', count)
					: this.trigger('countUnknown');
			})
			// We will get multiple events here; fire on just the first event
			.listenTo(
				this.selection,
				'change add remove reset',
				_.throttle(invalidate, 250, {trailing: false}),
			)
			// Keep up with destruction
			.listenTo(this, 'destroy', (model, collection, opts) => {
				//Object {remove: true, add: true, merge: true, index: 0}
				let count = this.pager.get('itemTotal');
				if (!count || isNaN(count)) count = 0;
				if (count) count--;
				this.pager.set('itemTotal', count);
			})
			// Remove deleted assets
			.listenTo(dispatcher, 'asset:deleted', (assetId) => {
				this.get(assetId) && this.remove(assetId);
			})
			// Listen for removal from category (if this happens to be a category asset collection)
			.listenTo(dispatcher, 'asset:moved:to:category', (assetId, newCat) => {
				if (!this.categoryId) return;
				let asset = this.get(assetId);
				if (asset && newCat !== this.categoryId) {
					this.remove(assetId);
				}
			})
			.listenTo(
				dispatcher,
				'asset:editor:edited smartLabels:updated',
				(assetId) => {
					if (!this.get(assetId)) return;
					this.reFetch({});
				},
			)
			// Listen for sort events
			.listenTo(dispatcher, 'user:sort', this.setSort);
	}

	/**
	 * @method
	 * @param {number} page Page
	 * @param {object} opts Options
	 */
	loadLocalAssets(startIndex = 0, stopIndex = 25, opts = {}) {
		let ids = localStore.get(this.localStoragePathKey);

		if (!ids || !ids.length) return new $.Deferred().resolve([]);

		const count = ids.length;
		this.pager.set('itemTotal', count);
		ids = ids.slice(Math.max(startIndex, 0), Math.min(stopIndex, count));

		return this.fetchSpecificAssets(ids, opts).then((results) => {
			this.trigger('counted', count);
			return results;
		});
	}

	/**
	 * @method
	 * @param {arrray} idArr IDs
	 */
	// todo: only fetch the assets we don't have
	// or only fetch when we arrive at the cart
	addLocalAssets(idArr) {
		let ids = localStore.get(this.localStoragePathKey);
		ids = _.union(idArr, ids);

		localStore.set(this.localStoragePathKey, ids);

		const count = ids.length;

		// keep the pager in the know
		this.pager.set('itemTotal', count);

		// fetch and add
		// this.loadLocalAssets(1);
		dispatcher.trigger('cart:added', idArr);

		this.trigger('sync', this, [], {}).trigger('counted', count);

		return new $.Deferred().resolve();
	}

	/**
	 * @method
	 * @param {arrray} idArr IDs
	 */
	removeLocalAssets(idArr) {
		let ids = this.getLocalAssetIdArray();
		ids = _.difference(ids, idArr);
		const count = ids.length;

		localStore.set(this.localStoragePathKey, ids);

		// keep the pager in the know
		this.pager.set('itemTotal', count);

		this.remove(idArr);
		dispatcher.trigger('cart:removed', idArr);

		this.trigger('sync', this, [], {}).trigger('counted', count);
	}

	/** @method */
	getLocalAssetIdArray() {
		return localStore.get(this.localStoragePathKey) || [];
	}

	/** @method */
	empty() {
		const ids = this.getLocalAssetIdArray();
		localStore.set(this.localStoragePathKey, []);
		dispatcher.trigger('cart:removed', ids);
		this.pager.set('itemTotal', 0);
		this.reset();
		this.trigger('counted', 0);
		return new $.Deferred().resolve();
	}

	/**
	 * assetCount() returns an asset count, if the count is known.  If the count isn't know,
	 * it returns undefined and triggers an asset count call to the DAM.  When the count is known,
	 * 'counted' will be triggered on the collection with the correct count as an argument.
	 *
	 * Views that wish to display a count should call assetCount(), and listen for the 'counted' event.
	 * @method
	 * @return {number|undefined} Asset count
	 */
	assetCount() {
		if (this.useFacetedSearch()) {
			if (!this.pager.get('itemTotal') && this.length) {
				this.pager.set('itemTotal', this.selection.searchFacets.itemTotal || 0);
			}
			return this.pager.get('itemTotal');
		}

		// Fixes false '0' occurences
		if (
			this.pager.has('itemTotal') &&
			this.pager.get('itemTotal') >= this.length
		) {
			return this.pager.get('itemTotal');
		}

		if (this.fetchingAssetCount || this.length) {
			return undefined;
		}

		this.pager.set('itemTotal', 0);
		return 0;
	}

	/**
	 * Destroy all assets in this collection -- here and on the server.
	 * @method
	 * @fires module:asset#asset:deleted
	 * @fires module:asset#asset:deleted:error
	 */
	destroyAll() {
		// Store for promises
		const promises = [];

		// Do not create functions in while loops
		const error = (assetId) => {
			dispatcher.trigger('asset:deleted:error', assetId);
			dispatcher.trigger('asset:deleted:error:' + assetId);
		};
		const success = (assetId) => {
			dispatcher.trigger('asset:deleted', assetId);
			dispatcher.trigger('asset:deleted:' + assetId);
		};

		// Loop over all assets in this collection
		let asset;
		while (this.length > 0) {
			asset = this.at(0);
			const aID = asset.get('assetId');
			promises.push(
				asset.destroy().then(_.partial(success, aID), _.partial(error, aID)),
			);
		}
		// Wait for all promises to return
		return $.when.apply($, promises);
	}

	/**
	 * @method
	 * @return {boolean}
	 */
	isSortable() {
		// Cannot sort on cart or custom search types currently
		var customSearchType = this.selection.findWhere({elementType: 9});
		return _.isUndefined(customSearchType);
	}

	/**
	 * Re-fetch all assets in collection
	 * @method
	 * @param {object} opts Fetch options
	 */
	reFetch(opts) {
		// See the wrapped fetch method in constructor
		const hasChunkSize = !!this.chunkSize;

		// Backup values
		const oldChunkSize = this.chunkSize;
		const oldMaxItems = this.selection.maxItems;

		// Change to the amount of assets we currently have
		hasChunkSize
			? (this.chunkSize = this.length)
			: (this.selection.maxItems = this.length + 1); // start positions begins at 1

		return this.fetch(opts).always(() => {
			// Restore values
			this.chunkSize = oldChunkSize;
			this.selection.maxItems = oldMaxItems;
		});
	}

	/**
	 * Respond to sort events
	 * @method
	 * @param {string} sortField Field to sort by
	 * @param {number} sortOrder Sort direction ( 0/1 )
	 */
	setSort(sortField, sortOrder) {
		this.selection.sortField = sortField;
		this.selection.sortOrder = sortOrder;
		this.trigger('sortChanged', this);
	}

	/**
	 * selection start position basec on paging
	 * @method
	 * defaults to 1
	 */
	setStartPosition() {
		if (this.curStartIndex != null) {
			this.selection.startPosition = this.curStartIndex + 1;
			return;
		}
		this.curPage = this.curPage || 1;
		// otherwise set start position based on page
		this.selection.startPosition = this.pageSize * (this.curPage - 1) + 1;
	}

	/**
	 * Create an AssetSet model describing this set of assets.
	 * This generic version returns just the current set of assets in this collection at this moment -- ignorant of paging, selection, etc.
	 * Subclasses (cartAssets, searchAssets, categoryAssets, etc) will override this to return more meaningful asset sets.
	 * @method
	 * @return {module:asset/models/assetSet} AssetSet
	 */
	toAssetSet() {
		return new AssetSetModel({
			type: assetSetTypes.asset,
			ids: this.ids(),
			label: i18n.t('i18n.nAssets', {count: this.length}),
			models: this.models,
		});
	}

	////////////////////////////////////
	// CHUNKS
	////////////////////////////////////

	/**
	 * For chunked loading of pages, addChunk loads the next not-yet-loaded section of a page.
	 * @method
	 * @param {object} opts Chunk fetch options
	 */
	addChunk(opts = {}) {
		if (
			// Do nothing if we're in the midst of a sync, or if we have no more chunks.
			this.syncing ||
			this.lastChunk ||
			this.pageSize == this.length ||
			this.length >= this.pageSize
		) {
			return new $.Deferred().resolve();
		}

		const lengthBefore = this.length;
		// disable the 'loading' class/div behaviour when loading a chunk
		opts.chunk = this.loadingChunk = true;

		return this.fetch(opts).then(() => {
			// if we didn't get anything new?
			if (this.length == lengthBefore && !this.lastChunk) {
				// Out-of-order fetches can occur, leading to a deadly chunk loop.
				// Clean that up by re-fetching the entire page so far.
				opts.refetch = true;
				return this.fetch(opts).then((response) => {
					delete this.loadingChunk;
					return this;
				});
			}

			delete this.loadingChunk;
			return this;
		});
	}

	////////////////////////////////////
	// !CHUNKS
	////////////////////////////////////

	////////////////////////////////////
	// FACETS
	////////////////////////////////////

	/**
	 * This checks that there are no invalid facet search types in our search rules\
	 * @method
	 * @return {boolean} Valid
	 */
	checkValidFacetSearchType() {
		let valid = true;
		this.selection.each((m) => {
			if (valid && _.indexOf(facetSupports, m.get('elementType')) < 0) {
				valid = false;
			}
		});

		return valid;
	}

	/**
	 * When an option is selected or deselected in our search facets, this function is called.
	 * By default it triggers a fetch.  Override it for different behavior.
	 * @method
	 * @param {Netx.Models.SearchFacet} model SearchFacet model that changed
	 * @param {object} opts Options
	 */
	facetSelected(model, collection, opts) {
		// Reset start position to 1
		this.selection.startPosition = 1;
		this.pager.gotoPage(1, true);
		this.fetch(_.extend({}, opts, {duplicateQueryPolicy: 'abort'}));
	}

	/**
	 * calling this.useFacetedSearch() with a boolean sets faceted search mode on or off,
	 * and returns the state.
	 * Calling it with no arguments just returns the state.
	 * @method
	 * @param {boolean} state true (on) or false (off)
	 * @param {object} opts options to set on the searchFacets child collection -- such as 'filterMode'
	 */
	useFacetedSearch(state, opts) {
		// Check that we are using a valid search type
		const validSearchType = this.checkValidFacetSearchType();
		// Let it set the things it would normally set regardless
		if (state != null) {
			if (state) {
				_.extend(this.selection.searchFacets, opts || {});
				// Listen for facet options to change
				this.stopListening(dispatcher, 'search:facet:option:changed').listenTo(
					dispatcher,
					'search:facet:option:changed',
					(model, collection) => {
						// Make sure we're the right collection:
						// added !this.hasRouteContext() because collection !== this.selection.searchFacets
						// results in false for both category and search asset collections
						// are they using the same facet collection? Not seeing ids to compare
						if (
							collection !== this.selection.searchFacets ||
							!this.hasRouteContext()
						)
							return;

						this.facetSelected.apply(this, arguments);
					},
				);
			} else {
				this.stopListening(dispatcher, 'search:facet:option:changed');
			}

			this._usingFacetedSearch = state;
			// This only matters if they have chosen a valid search type
			if (validSearchType) {
				if (!state) this.selection.searchFacets.turnOff();
				this.trigger('useFaceted', state, this);
			} else if (state && !validSearchType) {
				// Dispatch something to let the user know that facets have been disabled for this search type
			}
		}
		// Not valid
		// We do it like this so we are not actually turning facets off if a user wished to turn them on
		// we are just turning them off for this search type but will honor the users wishes to have facets on for
		// all other valid searches
		else if (!validSearchType) {
			// Dispatch something to let the user know that facets have been disabled for this search type
			this.selection.searchFacets.turnOff();
			return false;
		}
		// We made it
		return this._usingFacetedSearch;
	}
	////////////////////////////////////
	// !FACETS
	////////////////////////////////////

	////////////////////////////////////
	// PAGING
	////////////////////////////////////
	/**
	 * Determine if this collection is the one referred to in the route hash
	 * helpful for search facets, is this the right collection? have we changed contexts?
	 * should facets be reset or are we paging?  Is our context in the location.hash?
	 * @method
	 */
	hasRouteContext() {
		// Check for unescaped and escaped
		const hash = unescapeURI(window.location.hash);
		return (
			hash.indexOf(unescapeURI(this.routeContext)) > -1 ||
			hash.indexOf(escapeURI(this.routeContext)) > -1
		);
	}

	////////////////////////////////////
	// !PAGING
	////////////////////////////////////

	/** @type {string} */
	static get __name__() {
		return 'Assets';
	}
}
